import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Helmet from 'react-helmet';
import {
  Box,
  Button,
  Container,
  Heading,
  Spinner,
  Text,
} from '@chakra-ui/react';
import LogbookArticleView from '../components/logbook/LogbookArticleView';
import { fetchLogbookEntryBySlug } from '../utils/logbookApi';
import { toPlainText } from '../utils/toPlainText';

const PUBLIC_SITE_URL = (
  process.env.REACT_APP_PUBLIC_SITE_URL || 'https://www.cilassociazione.it'
).replace(/\/+$/, '');
const PUBLIC_MEDIA_URL = (
  process.env.REACT_APP_PUBLIC_MEDIA_URL ||
  process.env.REACT_APP_STRAPI_API_URL ||
  ''
).replace(/\/+$/, '');
const DEFAULT_OG_IMAGE = `${PUBLIC_SITE_URL}/og-default.svg`;

const toAbsoluteMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (!PUBLIC_MEDIA_URL) return '';
  return `${PUBLIC_MEDIA_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

const LogbookDetail = () => {
  const { slug } = useParams();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const run = async () => {
      try {
        setLoading(true);
        const response = await fetchLogbookEntryBySlug(slug);
        if (!active) return;
        setEntry(response.entry);
        setError('');
      } catch (err) {
        if (!active) return;
        setEntry(null);
        setError(err.message === 'not-found' ? 'not-found' : 'generic');
      } finally {
        if (active) setLoading(false);
      }
    };
    run();

    return () => {
      active = false;
    };
  }, [slug]);

  const seoTitle = useMemo(
    () =>
      toPlainText(entry?.metaTitle) ||
      toPlainText(entry?.title) ||
      'Diario di Bordo - CIL',
    [entry]
  );
  const seoDescription = useMemo(
    () =>
      toPlainText(entry?.metaDescription) || toPlainText(entry?.excerpt) || '',
    [entry]
  );
  const seoImage = useMemo(
    () =>
      toAbsoluteMediaUrl(entry?.ogImage?.url || entry?.heroImage?.url || ''),
    [entry]
  );

  const sharePageUrl = useMemo(
    () =>
      slug
        ? `${PUBLIC_SITE_URL}/logbook/${encodeURIComponent(slug)}`
        : `${PUBLIC_SITE_URL}/logbook`,
    [slug]
  );

  const ogImageForShare = useMemo(
    () => seoImage || DEFAULT_OG_IMAGE,
    [seoImage]
  );

  if (loading) {
    return (
      <Box
        minH="70vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner />
      </Box>
    );
  }

  if (!entry || error === 'not-found') {
    return (
      <Container py={12}>
        <Heading size="md">Diario non trovato</Heading>
        <Button as={Link} to="/logbook" mt={4}>
          Torna al logbook
        </Button>
      </Container>
    );
  }

  if (error) {
    return (
      <Container py={12}>
        <Text color="red.500">Errore nel caricamento del diario.</Text>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={sharePageUrl} />
        <meta property="og:type" content="article" />
        {ogImageForShare ? (
          <meta property="og:image" content={ogImageForShare} />
        ) : null}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        {ogImageForShare ? (
          <meta name="twitter:image" content={ogImageForShare} />
        ) : null}
      </Helmet>
      <LogbookArticleView entry={entry} />
    </>
  );
};

export default LogbookDetail;
